import { getIn } from 'formik';
import { cloneDeep, filter, flatMap, forEach, map } from 'lodash';
import React, { useContext } from 'react';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import {
  Container,
  ExtraWideTh,
  LeftTable,
  LeftTableContainer,
  NarrowTh,
  NoWrapTd,
  RightTable,
  RightTableContainer,
  WideTh,
} from '../../../../../shared/SplitTable';
import { TBody, THead, Tr } from '../../../../../shared/Table';
import { QuoteOrDesignFormContext } from '../../QuoteOrDesignFormContext';
import { floorLevelsByFloorLevelCode } from '../../quoteOrDesignFormData';
import { roomFieldName } from '../../quoteOrDesignFormFieldNameHelpers';
import { UnitFormModel } from '../unitFormModel';
import { RoomsFloorTable } from './RoomsFloorTable';
import { RoomsHeatLossTableViewType } from './RoomsHeatLossTableViewTypeButtons';
import { RoomsRoofTable } from './RoomsRoofTable';
import { RoomsWallTable } from './RoomsWallTable';

type Props = {
  unit: UnitFormModel;
  unitIndex: number;
  filteredManifoldId: string | null;
  selectedViewType: RoomsHeatLossTableViewType;
};

export const RoomsHeatLossTable = ({ unit, unitIndex, filteredManifoldId, selectedViewType }: Props) => {
  const formContext = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { setFieldValue, setFieldTouched } = formContext.formProps;

  const roomHeatLossDetailsFieldName = (manifoldIndex: number, roomIndex: number, fieldName: string) =>
    `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.${fieldName}`;

  const getRoomsToFillDetails = () => {
    if (unit.manifolds == null || unit.manifolds.length === 0) {
      return;
    }

    const manifoldsToFillDetails = filter(
      map(unit.manifolds, (manifold, manifoldIndex) => ({ manifold, manifoldIndex })),
      ({ manifold }) => filteredManifoldId === null || manifold.id === filteredManifoldId,
    );

    return flatMap(manifoldsToFillDetails, ({ manifold, manifoldIndex }) =>
      map(manifold.rooms, (room, roomIndex) => ({ manifoldIndex, roomIndex, room })),
    );
  };

  const fillDownColumn = (columnFieldName: string) => () => {
    const roomsToFillDetails = getRoomsToFillDetails();

    if (roomsToFillDetails === undefined || roomsToFillDetails.length === 0) {
      return;
    }

    const fieldName = `heatLossDetails.${columnFieldName}`;

    const valueToFill = cloneDeep(getIn(roomsToFillDetails[0].room, fieldName));

    forEach(roomsToFillDetails, ({ manifoldIndex, roomIndex }) => {
      setFieldValue(roomFieldName(unitIndex, manifoldIndex, roomIndex, fieldName), valueToFill, false);
      setFieldTouched(roomFieldName(unitIndex, manifoldIndex, roomIndex, fieldName), true);
    });
  };

  return (
    <Container>
      <LeftTableContainer>
        <LeftTable>
          <THead>
            <Tr>
              <WideTh rowSpan={2}>Manifold</WideTh>
              <NarrowTh rowSpan={2}>Floor Level</NarrowTh>
              <ExtraWideTh rowSpan={2}>Room</ExtraWideTh>
            </Tr>
            <Tr />
          </THead>
          <TBody>
            {unit.manifolds
              .map((manifold, manifoldIndex) => ({ manifold, manifoldIndex }))
              .filter(({ manifold }) => filteredManifoldId === null || manifold.id === filteredManifoldId)
              .map(({ manifold, manifoldIndex }) =>
                manifold.rooms == null
                  ? null
                  : manifold.rooms.map((room, roomIndex) => (
                      <Tr key={`${manifoldIndex}-${roomIndex}`}>
                        <NoWrapTd>{manifold.name || 'Unnamed Manifold'}</NoWrapTd>
                        <NoWrapTd>
                          {room.floorLevelCode ? floorLevelsByFloorLevelCode[room.floorLevelCode] : '-'}
                        </NoWrapTd>
                        <NoWrapTd>{room.name || 'Unnamed Room'}</NoWrapTd>
                      </Tr>
                    )),
              )}
          </TBody>
        </LeftTable>
      </LeftTableContainer>
      <RightTableContainer>
        <RightTable>
          {selectedViewType === 'Roof' && (
            <RoomsRoofTable
              unit={unit}
              filteredManifoldId={filteredManifoldId}
              fillDownColumn={fillDownColumn}
              roomHeatLossDetailsFieldName={roomHeatLossDetailsFieldName}
            />
          )}
          {selectedViewType === 'Wall' && (
            <RoomsWallTable
              unit={unit}
              filteredManifoldId={filteredManifoldId}
              fillDownColumn={fillDownColumn}
              roomHeatLossDetailsFieldName={roomHeatLossDetailsFieldName}
            />
          )}
          {selectedViewType === 'Floor' && (
            <RoomsFloorTable
              unit={unit}
              unitIndex={unitIndex}
              filteredManifoldId={filteredManifoldId}
              fillDownColumn={fillDownColumn}
              roomHeatLossDetailsFieldName={roomHeatLossDetailsFieldName}
              getRoomsToFillDetails={getRoomsToFillDetails}
            />
          )}
        </RightTable>
      </RightTableContainer>
    </Container>
  );
};
